import { WALL_THICKNESS } from "../utils/constants.js";

// Cut surfaces: off the camera, still in the shadow pass, so the room's light never changes.
export const CUTAWAY_LAYER = 1;

// Cut a little early so the near plane never slices a surface.
const NEAR_MARGIN = 0.3;

const BIT = { north: 1, south: 2, east: 4, west: 8, ceiling: 16 };

/** Cuts each wall the camera is past (with its `wallName`-tagged parts), and the ceiling once any wall is open or the camera is above it. */
export class RoomCutaway {
  constructor(app, renderer = null) {
    this.app = app;
    this._cut = 0;
    this._walls = null;
    if (renderer?.shadowMap) castCutawayShadows(renderer);
  }

  update(camera) {
    const sceneManager = this.app?.getSceneManager?.();
    const roomBuilder = sceneManager?.getRoomBuilder();
    const scene = sceneManager?.getScene();
    if (!roomBuilder || !scene || !camera) return;

    const walled = roomBuilder.walls.some((wall) => wall.visible);
    const cut = walled ? this._cutFor(camera.position, roomBuilder.roomSize) : 0;
    // A rebuilt room arrives on the default layer.
    if (cut === this._cut && roomBuilder.walls === this._walls) return;

    this._cut = cut;
    this._walls = roomBuilder.walls;
    this._apply(scene, roomBuilder, cut);
  }

  /** True while the camera looks into a walled room from outside it. */
  isOpen() {
    return this._cut !== 0;
  }

  _cutFor(position, { width, depth, height }) {
    const halfWidth = width / 2 - WALL_THICKNESS / 2 - NEAR_MARGIN;
    const halfDepth = depth / 2 - WALL_THICKNESS / 2 - NEAR_MARGIN;
    let cut = 0;
    if (position.z < -halfDepth) cut |= BIT.north;
    if (position.z > halfDepth) cut |= BIT.south;
    if (position.x > halfWidth) cut |= BIT.east;
    if (position.x < -halfWidth) cut |= BIT.west;
    if (cut || position.y > height - NEAR_MARGIN) cut |= BIT.ceiling;
    return cut;
  }

  _apply(scene, roomBuilder, cut) {
    for (const object of scene.children) {
      const bit = BIT[object.userData.wallName];
      if (bit) setCut(object, (cut & bit) !== 0);
    }
    if (roomBuilder.ceiling) setCut(roomBuilder.ceiling, (cut & BIT.ceiling) !== 0);
    // The tracer ignores layers, so it drops cut surfaces itself on a rebuild.
    this.app.getPathTracingManager?.()?.invalidate();
  }
}

/** three filters shadow casters by the viewing camera's layers, so the cut layer is enabled for just the shadow pass. */
function castCutawayShadows(renderer) {
  const shadowMap = renderer.shadowMap;
  const render = shadowMap.render.bind(shadowMap);
  shadowMap.render = (lights, scene, camera) => {
    if (camera.layers.isEnabled(CUTAWAY_LAYER)) return render(lights, scene, camera);
    camera.layers.enable(CUTAWAY_LAYER);
    try {
      render(lights, scene, camera);
    } finally {
      camera.layers.disable(CUTAWAY_LAYER);
    }
  };
}

function setCut(root, cut) {
  root.traverse((object) => object.layers.set(cut ? CUTAWAY_LAYER : 0));
}
